import { useEffect, useState } from "react"
import type { MapboxGeoJSONFeature } from "mapbox-gl"
import type { IParcel } from "@/types/explore"
import type { PluInfoType } from "@/types/explore"
import type { POIMarker, MutationMarker } from "@/types/explore"
import { useGeorisques } from "@/hooks/explore/use-georisques"
import { useUrbanisme } from "@/hooks/explore/use-urbanisme"
import { usePois } from "@/hooks/explore/use-pois"
import { geoFetch } from "@/lib/explore/api/geo-fetch"
import { Button } from "@/components/ui/button"
import { MapPinIcon, XIcon } from "lucide-react"
import { CopyButton } from "./copy-button"
import { ExternalLinks } from "./external-links"
import { KeyIndicators } from "./key-indicators"
import { ParcelCardDetails } from "./parcel-card-details"

interface SingleParcelViewProps {
  selectedParcel: IParcel
  selectedPLU: MapboxGeoJSONFeature | null
  elevation: number | null
  ownerType: string | null
  onFlyTo: () => void
  onClose: () => void
  onCreateFolder: () => void
  setParcelPOI: (poi: { lon: number | null; lat: number | null }) => void
  onPLULoaded: (plu: PluInfoType | null) => void
  onPoiFlyTo?: (center: [number, number]) => void
  onActivePOIsChange?: (pois: POIMarker[]) => void
  onHoverPOI?: (poi: POIMarker | null) => void
  onActiveMutationsChange?: (markers: MutationMarker[]) => void
}

export const SingleParcelView = ({
  selectedParcel,
  selectedPLU,
  elevation,
  ownerType,
  onFlyTo,
  onClose,
  onCreateFolder,
  setParcelPOI,
  onPLULoaded,
  onPoiFlyTo,
  onActivePOIsChange,
  onHoverPOI,
  onActiveMutationsChange,
}: SingleParcelViewProps) => {
  const buildingData = selectedParcel?.buildings ?? null
  const {
    data: risquesData,
    loading: risquesLoading,
    error: risquesError,
  } = useGeorisques(selectedParcel?.center ?? null)
  const {
    data: urbanismeData,
    loading: urbanismeLoading,
    error: urbanismeError,
  } = useUrbanisme(selectedParcel?.center ?? null)
  const {
    pois,
    loading: poisLoading,
    error: poisError,
  } = usePois(
    selectedParcel?.center ?? null,
    selectedParcel?.properties?.code_insee ?? null,
    selectedParcel?.properties?.code_dep ?? null,
  )

  const [effectivePLU, setEffectivePLU] = useState<PluInfoType | null>(null)
  const [pluLoading, setPluLoading] = useState(false)

  const center = selectedParcel?.center ?? null
  useEffect(() => {
    if (!center) {
      setEffectivePLU(null)
      return
    }
    const [lon, lat] = center
    setPluLoading(true)
    const url = `https://www.geoportail-urbanisme.gouv.fr/api/feature-info/du?lon=${lon}&lat=${lat}&type=du&typeName=zone_urba`
    geoFetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`GPU API error: ${res.status}`)
        return res.json()
      })
      .then((json) => {
        const features = json?.features || json
        const feature = Array.isArray(features) ? features[0] : features
        const props = feature?.properties || feature
        if (props?.libelle) {
          const plu: PluInfoType = {
            libelle: props.libelle,
            libelong: props.libelong,
            typezone: props.typezone,
            gpu_timestamp: props.datvalid,
            gpu_doc_id: props.gpu_doc_id,
            nomfic: props.nomfic,
            partition: props.partition,
          }
          setEffectivePLU(plu)
          onPLULoaded(plu)
        } else {
          setEffectivePLU(null)
          onPLULoaded(null)
        }
      })
      .catch(() => {
        setEffectivePLU(null)
        onPLULoaded(null)
      })
      .finally(() => setPluLoading(false))
  }, [center?.[0], center?.[1]])

  return (
    <>
      <div className="border-t border-gray-200" />
      {/* Parcel header */}
      <div className="px-3 pt-3 pb-2 shrink-0 anim-fade-in">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-2.5 min-w-0 flex-1">
            <div className="w-10 h-10 bg-primary/10 text-primary rounded-md flex items-center justify-center text-base font-bold shrink-0">
              {selectedParcel.properties.section}
            </div>
            <div className="min-w-0">
              <div className="text-base font-bold text-gray-900 flex items-center gap-1.5">
                {selectedParcel.properties.section} {selectedParcel.properties.numero}
                <CopyButton
                  text={`${selectedParcel.properties.section} ${selectedParcel.properties.numero}`}
                  size="md"
                  title="Copier la reference"
                />
              </div>
              <div className="text-sm text-gray-500 mt-0.5 flex items-center gap-1">
                <span className="truncate">{selectedParcel.fullAddress || selectedParcel.address || ""}</span>
                {(selectedParcel.fullAddress || selectedParcel.address) && (
                  <CopyButton
                    text={selectedParcel.fullAddress || selectedParcel.address || ""}
                    title="Copier l'adresse complete"
                  />
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-1 shrink-0">
            <Button
              onClick={onFlyTo}
              variant="ghost"
              size="icon-sm"
              className="text-gray-400 hover:text-primary hover:bg-gray-100"
              title="Centrer"
            >
              <MapPinIcon />
            </Button>
            <Button
              onClick={onClose}
              variant="ghost"
              size="icon-sm"
              className="text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              title="Fermer"
            >
              <XIcon />
            </Button>
          </div>
        </div>
      </div>

      {/* Key indicators */}
      <div className="px-2 pb-1">
        <KeyIndicators selectedParcel={selectedParcel} elevation={elevation} ownerType={ownerType} />
      </div>

      {/* Scrollable details */}
      <div className="flex-1 overflow-y-auto scrollbar-thin" style={{ scrollbarGutter: "stable" }}>
        <ParcelCardDetails
          selectedParcel={selectedParcel}
          selectedPLU={selectedPLU}
          setParcelPOI={setParcelPOI}
          onPLULoaded={onPLULoaded}
          onFlyTo={onPoiFlyTo}
          onActivePOIsChange={onActivePOIsChange}
          onHoverPOI={onHoverPOI}
          onActiveMutationsChange={onActiveMutationsChange}
          effectivePLU={effectivePLU}
          pluLoading={pluLoading}
          buildingData={buildingData}
          risquesData={risquesData as Record<string, unknown> | null}
          risquesLoading={risquesLoading}
          risquesError={risquesError}
          urbanismeData={urbanismeData as Record<string, unknown> | null}
          urbanismeLoading={urbanismeLoading}
          urbanismeError={urbanismeError}
          pois={pois}
          poisLoading={poisLoading}
          poisError={poisError}
        />
      </div>

      {/* Sticky CTA footer */}
      <div className="px-3 py-2.5 border-t border-gray-200 bg-white shrink-0 rounded-b-lg space-y-2">
        <button
          onClick={onCreateFolder}
          className="w-full cursor-pointer bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary-dark transition-all shadow-sm hover:shadow-md btn-press h-11"
        >
          Creer un dossier Kaptcher
        </button>
        <ExternalLinks parcel={selectedParcel} />
      </div>
    </>
  )
}
